import {useMemo} from 'react';
import styled from 'styled-components';
import {Typography} from '@/components/Typography';
import {GroupAgePopulation} from '@/map/@types';
import {Flexbox, FlexFullGrow} from '@/styled-components';
import {palette} from '@/styles';
import {humanizeNumber} from '@/utils';
import IconMax from './chart-max-sum.png';
import IconMin from './chart-min-sum.png';
import {ColorCircle} from './StyledComponents';

interface IProps {
  labels: string[];
  data: GroupAgePopulation[];
}

const LEGENDS = [
  {color: palette.blue400, label: '남'},
  {color: palette.red400, label: '여'},
];

export const BarChart = ({labels, data: rawdata}: IProps) => {
  const {items, sorted} = useMemo(() => {
    if (!rawdata) {
      return {items: [], sorted: []};
    }

    const data = rawdata.map((i, index) => ({
      ...i,
      index,
      label: labels[index],
    }));

    return {
      items: data,
      sorted: [...data].sort((a, b) => a.sum - b.sum),
    };
  }, [rawdata, labels]);

  const maxItem = sorted[sorted.length - 1];
  const minItem = sorted[0];
  const maxSum = maxItem.sum;
  const minSum = minItem.sum;

  return (
    <Root>
      <Flexbox>
        <FlexFullGrow />
        <Typography variant="medium12" color={palette.gray500}>
          최고 {humanizeNumber(maxSum) || 0} 명 • 최저{' '}
          {humanizeNumber(minSum) || 0} 명
        </Typography>
      </Flexbox>
      <ChartWrapper>
        {items.map(({male, female, sum, index, label}) => {
          const maxValue = Math.max(female, male);
          const totalPercentage = maxSum ? (sum / maxSum) * 100 : 0;
          const totalHeight =
            sum > 0 && totalPercentage < 1 ? 1 : totalPercentage; // 값이 0이 아닌데 상대적으로 퍼센트가 너무 낮은 경우 1로 고정
          const malePercentage = maxValue ? (male / maxValue) * 100 : 0;
          const femalePercentage = maxValue ? (female / maxValue) * 100 : 0;
          const isMax = maxItem.sum === sum;
          const isMin = minItem.sum === sum;

          return (
            <BarWrapper key={`${sum}-${index}`}>
              <Bars style={{height: `${totalHeight}%`}}>
                {(isMax || isMin) && (
                  <MaxMinIcon>
                    <img
                      width={32}
                      height={27}
                      src={isMax ? IconMax : IconMin}
                      alt={isMax ? '최고' : '최저'}
                    />
                  </MaxMinIcon>
                )}
                <Bar
                  style={{
                    height: `${malePercentage}%`,
                    backgroundColor: LEGENDS[0].color,
                  }}
                />
                <Bar
                  style={{
                    height: `${femalePercentage}%`,
                    backgroundColor: LEGENDS[1].color,
                  }}
                />
              </Bars>
              <BarLabel>{label}</BarLabel>
            </BarWrapper>
          );
        })}
      </ChartWrapper>
      <LegendWrapper>
        <FlexFullGrow />
        {LEGENDS.map((i) => (
          <LegendLabel key={i.label}>
            <ColorCircle color={i.color} />
            <div>{i.label} </div>
          </LegendLabel>
        ))}
      </LegendWrapper>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px 16px 16px;
  border-radius: 8px;
  background-color: ${palette.gray100};
  margin-top: 16px;
`;

const ChartWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  height: 188px;
  padding: 68px 0 12px;
`;

const BarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const Bars = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 2px;
`;

const MaxMinIcon = styled.div`
  position: absolute;
  top: -35px;
`;

const Bar = styled.div`
  width: 12px;
  border-top-right-radius: 2px;
  border-top-left-radius: 2px;
`;

const BarLabel = styled.div`
font-size: 11px;
color: ${palette.gray600}
text-align: center;
font-weight: 500;
line-height: 150%;
letter-spacing: -0.3px;
`;

const LegendWrapper = styled(Flexbox)`
  gap: 12px;
  height: 14px;
  color: ${palette.gray800};
  font-size: 12px;
  font-weight: 400;
`;

const LegendLabel = styled(Flexbox)`
  gap: 6px;
`;
